<template>
    <div class="h-[calc(100vh-106px)] bg-[#0c1116] flex pb-6">

        <!-- Left Section: Transcript List -->
        <div class="w-[400px] max-[800px]:w-full border-r border-[#141c24] flex flex-col" v-if="!$isMobile() || !$route.params.id">
          <div class="px-4 pt-6 pb-3">
            <div class="flex justify-between mb-3">
              <div class="font-inter text-[19px] text-white font-semibold leading-[30px]">{{ $t('transcripts_title')}}</div>
              <div v-if="filteredGroupedTranscripts">
                <button @click="isEditing = !isEditing" class="font-inter text-[14px] font-medium leading-[20px] not-italic cursor-pointer mr-2" :class="[isEditing ? 'text-[#6CBFFF]' : 'text-white/55']">
                  {{ isEditing ? $t('common_cancel') : $t('common_edit') }}
                </button>
              </div>
            </div>
            <div class="relative mb-3 max-[800px]:mb-[18px]" v-if="!filteredGroupedTranscripts">
              <input
                v-model="searchQuery" 
                type="text" 
                name="search" 
                class="border-2 border-[#292F36] rounded-[10px] bg-transparent text-white font-normal text-base leading-[1.18rem] h-[44px] w-full pl-10 pr-[10px] outline-none focus:border-[#6CBFFF]" 
                placeholder="Search list" 
                id="search_list"/>
              <img src="@/assets/icons/search.svg" class="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2">
            </div>
          </div>
          <div class="flex-1 overflow-y-scroll scrollbar-thumb-rounded-full scrollbar-thin scrollbar-track-rounded-full scrollbar scrollbar-thumb-[#f0f6ff57] scrollbar-track-transparent px-4">
              <div v-if="filteredGroupedTranscripts">
                  <div v-for="(items, monthKey) in filteredGroupedTranscripts" :key="monthKey">
                      <div class="font-inter text-[14px] font-medium leading-[20px] not-italic mt-6 text-white/55 px-3 sticky top-0 bg-[#0c1116] z-9">
                          {{ formatMonthLabel(monthKey) }}
                      </div>
                      <div v-for="item in items" :key="item.conference_id">
                        <div @click="selectTranscript(item.conference_id)" class="flex justify-between text-base leading-[1.18rem] text-white hover:bg-[#1e2428] cursor-pointer  mb-2 p-3 rounded-[12px]" :class="[selectedTranscript.id === item.conference_id ? 'bg-[#1e2428]' : 'bg-none']">
                          <div class="flex flex-col">
                              <span class="text-[16px]">{{ item.title }}</span>
                              <span class="text-[14px] text-white/75 mt-1">{{ formatDate(item.timestamp) }}</span>
                          </div>
                          <div v-if="isEditing" class="flex items-center">
                              <button @click="openDeleteTranscriptPopup(item.conference_id)" class="cursor-pointer">
                                  <img src="@/assets/icons/trash.svg" class="w-5 h-5">
                              </button>
                          </div>
                        </div>
                      </div>
                  </div>
              </div>
              <div v-if="searchQuery && Object.keys(filteredGroupedTranscripts).length === 0" class="text-center text-white/75 mt-6">
                  {{ $t('no_results_for_message') }} "{{ searchQuery }}"
              </div>
              <div v-if="!searchQuery && Object.keys(filteredGroupedTranscripts).length === 0" class="text-center text-white/75 mt-6 flex flex-col gap-4 justify-center items-center">
                <img src="@/assets/icons/sorenson/file.svg" alt="" class="w-8">
                <span>{{ $t('no_transcripts_message')}}</span>
              </div>
          </div>
        </div>

        <!-- Right Section: Transcript Content -->
        <div class="flex-1 mt-0 md:mt-15 flex scrollbar-thumb-rounded-full scrollbar-thin scrollbar-track-rounded-full scrollbar scrollbar-thumb-[#f0f6ff57] scrollbar-track-transparent overflow-y-scroll " v-if="selectedTranscript.id">

          <div v-if="selectedTranscript.id" class="w-full max-w-[800px] px-8 md:px-8 sm:px-4 mx-auto">
            <div class="flex md:hidden justify-between items-center mt-4 mb-4">
              <a href="javascript:;" @click="back" class="flex p-2">
                <img src="@/assets/icons/arrow-left.svg" class="w-[25px]" />
              </a>
              <div class="font-inter text-[16px] font-semibold leading-[24px] not-italic text-white/89 ">{{ $t('transcript_title') }}</div>
              <div class="relative">
                <button @click="settingsMenuOpen = !settingsMenuOpen" class="hover:bg-[#203547] rounded-[10px] p-2 cursor-pointer" :class="[settingsMenuOpen ? 'bg-[#203547]' : 'bg-none']">
                  <img src="@/assets/icons/sorenson/dots-vertical-primary.svg" alt="Open Settings">
                </button>
                <div v-if="settingsMenuOpen" class="absolute right-0 mt-1 w-24 bg-[#171d24] rounded-[12px] shadow-lg z-10">
                  <ul>
                    <li class="text-white/89 flex flex-1 justify-center items-center text-center no-underline cursor-pointer px-4 py-2 text-sm" @click="setTitleEditingModal(true)">
                      <img src="@/assets/icons/sorenson/pencil.svg" alt="Edit Title" class="max-w-[20px] mr-[5px]">
                      {{ $t('common_edit') }}
                    </li>
                    <li class="text-white/89 flex flex-1 justify-center items-center text-center no-underline cursor-pointer px-4 py-2 text-sm" @click="shareTranscript(selectedTranscript.id)">
                      <img src="@/assets/icons/share.svg" alt="Share" class="max-w-[20px] mr-[5px]">
                      {{ $t('common_share') }}
                    </li>
                    <li class="text-[#FF6566] flex flex-1 justify-center items-center text-center no-underline cursor-pointer px-4 py-2 text-sm gap-1" @click="openDeleteTranscriptPopup(selectedTranscript.id)">
                      <img src="@/assets/icons/trash.svg" alt="Delete" class="max-w-[20px]">
                      {{ $t('commont_delete') }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="flex justify-between">
              <header class="flex gap-3 items-center">
                <div class="text-white text-[19px] font-semibold leading-[30px]" v-if="!isTitleEditing">{{ selectedTranscript.title }}</div>
                <div class="mb-2" v-else>
                  <input
                      v-model="selectedTranscript.title"
                      type="text"
                      name="transcript-title"
                      @input="titleError = false"
                      class="border-2 rounded-[10px] bg-transparent text-white font-normal text-base leading-[1.18rem] h-[44px] w-full pl-[10px] pr-[10px] outline-none"
                      :class="titleError ? 'border-red-500' : 'border-[#292F36] focus:border-[#6CBFFF]'"
                      />
                </div>
                <div v-if="!isTitleEditing">
                  <button class="cursor-pointer hidden md:flex" @click="setTitleEditing(true)">
                    <img src="@/assets/icons/sorenson/pencil.svg" alt="">
                  </button>
                </div>
                <div class="font-inter text-[14px] font-medium leading-[20px] not-italic cursor-pointer mb-2 text-primary" v-else @click="saveTranscriptTitle">
                  {{ $t('common_save') }}
                </div>
              </header>
              <div class="relative hidden md:block">
                <button @click="settingsMenuOpen = !settingsMenuOpen" class="hover:bg-[#203547] rounded-[10px] p-1 cursor-pointer" :class="[settingsMenuOpen ? 'bg-[#203547]' : 'bg-none']">
                  <img src="@/assets/icons/sorenson/dots-vertical.svg" alt="Open Settings">
                </button>
                <div v-if="settingsMenuOpen" class="absolute right-0 mt-1 w-24 bg-[#171d24] rounded-[12px] shadow-lg z-10">
                  <ul>
                     <li class="text-white/89 flex flex-1 justify-center items-center text-center no-underline cursor-pointer px-4 py-2 text-sm" @click="shareTranscript(selectedTranscript.id)">
                      <img src="@/assets/icons/share.svg" alt="Share" class="max-w-[20px] mr-[5px]">
                      {{ $t('common_share') }}
                    </li>
                    <li class="text-[#FF6566] flex flex-1 justify-center items-center text-center no-underline cursor-pointer px-4 py-2 text-sm gap-1" @click="openDeleteTranscriptPopup(selectedTranscript.id)">
                      <img src="@/assets/icons/trash.svg" alt="Delete" class="max-w-[20px]">
                      {{ $t('commont_delete') }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="font-inter text-[14px] font-medium leading-[20px] not-italic text-white/55 mt-1">{{ userName }}</div>
            <div class="font-inter text-[12px] leading-[20px] not-italic text-white/55 mt-1">{{ formatCreationDate(selectedTranscript.timestamp) }}</div>
            <div v-for="(group, index) in groupedMessages" :key="index" class="text-white/75">
              <div class="font-inter text-[12px] leading-[18px] not-italic text-primary mt-6 font-semibold sticky top-0 bg-[#0c1116] z-9 mb-1">{{ group.speaker }}</div>
              <div v-for="(msg, idx) in group.messages" :key="idx" class="mb-2">
                <div class="font-inter text-[14px] font-medium leading-[20px] not-italic text-white/89">{{ msg.source }}</div>
                <div v-if="msg.translations && Array.isArray(msg.translations)">
                  <div v-for="(translation, tIdx) in msg.translations" :key="tIdx" class="font-inter text-[14px] leading-[20px] not-italic text-white/75 ml-3 mt-1">
                    {{ translation.text }}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
    </div>

    <sheet-modal v-model="isDeleting" exact-width="w-[350px]">
        <div class="flex flex-col items-center">
            <img src="@/assets/icons/caution.svg" class="w-14 h-14 mb-7">
            <h3 class="font-inter text-[19px] font-semibold leading-[30px] mb-2 text-white">{{ $t('transcripts_delete_title') }}</h3>
            <p class="text-sm text-white/75 mb-7">{{ $t('transcripts_delete_description') }}</p>
            <div class="flex flex-col w-full">
              <button @click="deleteTranscript(deleteTranscriptId)" class="self-stretch px-5 py-2.5 bg-[#FF6566] hover:bg-opacity-30 rounded-xl inline-flex justify-center items-center gap-2 overflow-hidden transition duration-160 cursor-pointer">
                  <div class="justify-start text-black text-base font-medium font-['Inter'] leading-normal">
                      {{ $t('commont_delete') }}
                  </div>
              </button>
              <div @click="isDeleting = false" class="text-[#6CBFFF] text-base leading-[1.2rem] font-inter mt-5 mb-2 text-center cursor-pointer">
                {{ $t('common_cancel') || 'Cancel' }}
              </div>  
            </div>
        </div>
    </sheet-modal>
    <sheet-modal v-model="showShareModal" @closed="showShareModal = false" scrollable exact-width="w-[400px]">
      <div class="flex flex-col">
        <img src="@/assets/icons/sorenson/copy.svg" class="w-14 h-14 mb-7 self-center">
        <h3 class="font-inter text-[19px] font-semibold leading-[30px] mb-2 text-white text-center">{{ $t('transcripts_share_title') }}</h3>
        <p class="text-sm text-white/75 mb-7">{{ $t('transcripts_share_description') }}</p>
        <SwitchGroup as="div" class="flex justify-between mb-5">
          <span class="flex grow flex-col">
            <SwitchLabel as="span" class="font-medium text-white" passive>{{ $t('transcript_make_public') }}</SwitchLabel>
            <SwitchDescription as="span" class="text-sm text-neutral-400">{{ $t('transcript_make_public_desc') }}</SwitchDescription>
          </span>
          <Switch v-model="selectedTranscript.is_shared" :class="[selectedTranscript.is_shared ? 'bg-primary' : 'bg-gray-700', 'relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden']">
            <span aria-hidden="true" :class="[selectedTranscript.is_shared ? 'translate-x-5 bg-gray-800' : 'translate-x-0 bg-white', 'pointer-events-none inline-block size-5 transform rounded-full shadow-sm ring-0 transition duration-200 ease-in-out mt-0.5']" />
          </Switch>
        </SwitchGroup>
        <div class="flex flex-col w-full">
          <base-button level="secondary" size="medium" :text="$t('copy_url')" :disabled="!selectedTranscript.is_shared" @click.prevent="copyShareUrl"></base-button>
          <div class="text-primary text-base leading-[1.2rem] font-inter mt-5 mb-2 text-center cursor-pointer" @click="showShareModal = false">{{ $t('common_cancel')}}</div>
        </div>
      </div>
    </sheet-modal>

    <sheet-modal v-model="isTitleEditingModal" @closed="isTitleEditingModal = false" scrollable exact-width="w-[400px]">
      <div class="flex flex-col">
        <h3 class="font-inter text-[19px] font-semibold leading-[30px] mb-2 text-white text-center">{{ $t('common_edit') }}</h3>
        <div class="mb-3 max-[800px]:mb-[18px]">
          <label class="block text-white/90 text-base leading-[1.18rem] mb-2 pl-[10px]" for="#transcript_title">{{ $t('session_setup_title')}}</label>
          <textarea
              class="border-2 rounded-[10px] bg-transparent text-white font-normal text-base leading-[1.18rem] w-full px-3 py-[10px] outline-none"
              :class="titleError ? 'border-red-500' : 'border-[#292F36] focus:border-primary'"
              id="transcript_title"
              v-model="selectedTranscript.title"
              @input="titleError = false">

          </textarea>
        </div>
        <div class="flex flex-col w-full">
          <base-button level="primary" size="medium" :text="$t('common_save')"  @click.prevent="saveTranscriptTitle"></base-button>
        </div>
      </div>
    </sheet-modal>

</template>

<script>
import { mapState } from 'vuex';
import TranscriptService from "@/services/transcript.service";
import SheetModal from '@/components/ui/SheetModal.vue';
import {Switch, SwitchDescription, SwitchGroup, SwitchLabel} from "@headlessui/vue";
import BaseButton from "@/components/ui/BaseButton.vue";
import { formatSessionBeganLabel, formatTranscriptListDate, formatTranscriptMonthLabel } from "@/utils/transcript-date";

export default {
  name: 'TranscriptsView',
  components: {
    BaseButton,
    Switch,
    SwitchDescription,
    SwitchLabel,
    SwitchGroup,
    SheetModal
  },
  data() {
    return {
      transcripts: [],
      isEditing: false,
      isDeleting: false,
      titleError: false,
      settingsMenuOpen: false,
      deleteTranscriptId: null,
      searchQuery: '',
      selectedTranscript: {
        id: null,
        title: '',
        creationDate: '',
        messages: [],
        is_shared: false,
        shared_url: ""
      },
      userName: '',
      isTitleEditing: false,
      showShareModal: false,
      isTitleEditingModal: false,
    }
  }, 

  methods: {
    openDeleteTranscriptPopup(id){
      this.settingsMenuOpen = false;
      this.deleteTranscriptId = id;
      this.isDeleting = true;
    },
    deleteTranscript(conference_id){
      this.isDeleting = false;
      this.selectedTranscript = {
        id: null,
        title: '',
        creationDate: '',
        messages: [],
      };
      TranscriptService.deleteTranscript(conference_id, this.token).then(() => {
        this.transcripts = this.transcripts.filter(item => item.conference_id !== conference_id);
      });
    },
    formatDate(timestamp) {
      return formatTranscriptListDate(timestamp);
    },
    formatMonthLabel(monthKey) {
      return formatTranscriptMonthLabel(monthKey);
    },
    formatCreationDate(dateString) {
      return formatSessionBeganLabel(dateString);
    },
    selectTranscript(id) {
      this.isTitleEditing = false;
      this.isTitleEditingModal = false;
      this.titleError = false;
      this.$router.push({
        name: 'transcript',
        params: { id: id }
      });
      TranscriptService.getTranscript(id, this.token).then((data) => {
        const transcriptData = data.data.result;
        this.selectedTranscript.id = id;
        this.selectedTranscript.title = transcriptData.title;
        this.selectedTranscript.messages = transcriptData.messages.reverse();
        this.selectedTranscript.creationDate = transcriptData.creation_date;
        this.selectedTranscript.conference_id = transcriptData.conference_id;
        this.selectedTranscript.is_shared = transcriptData.is_shared;
        this.selectedTranscript.shared_url = transcriptData.shared_url;
        this.selectedTranscript.timestamp = transcriptData.timestamp;
      });
    }, 
    back(){
      this.$router.back();
    },
    setTitleEditing(bool) {
      this.isTitleEditing = bool;
      this.titleError = false;
    },
    async saveTranscriptTitle() {
      if (!this.selectedTranscript.title || !this.selectedTranscript.title.trim()) {
        this.titleError = true;
        return;
      }
      this.titleError = false;
      await TranscriptService.updateTranscript(this.selectedTranscript, this.token).then(() => {
        this.$toast.success('transcript_title_saved_msg');
        this.isTitleEditing = false;
        this.isTitleEditingModal = false;
      });
    },
    shareTranscript(){
      this.showShareModal = true;
      this.settingsMenuOpen = false
    },
    async copyShareUrl() {
      try {
        await navigator.clipboard.writeText(this.selectedTranscript.shared_url);
        this.$toast.success('transcript_share_copied_msg');
        this.showShareModal = false;
      } catch (error) {
        console.error(error.message);
      }
    },
    setTitleEditingModal(bool) {
      this.isTitleEditingModal = bool;
      this.settingsMenuOpen = false;
      this.titleError = false;
    },
  },

  computed: {
    ...mapState('auth', ['token', 'authorized', 'agreed']),
    ...mapState('nonpersist', ['user']),
    groupedTranscripts() {
      const groups = {};
      for (const item of this.transcripts) {
        const date = new Date(item.timestamp);
        const key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`; //YYYY-MM
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      }
      console.log("groupedTranscripts: ", groups);
      return groups;
    },
    filteredGroupedTranscripts() {
      if (!this.searchQuery.trim()) {
        return this.groupedTranscripts;
      }
      
      const query = this.searchQuery.toLowerCase().trim();
      const filteredTranscripts = this.transcripts.filter(item => 
        item.title.toLowerCase().includes(query)
      );
      
      const groups = {};
      for (const item of filteredTranscripts) {
        const date = new Date(item.created_date);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; //YYYY-MM
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      }
      
      return groups;
    },
    groupedMessages() {
      const groups = [];
      let lastSpeaker = null;
      let currentGroup = null;
      for (const msg of this.selectedTranscript.messages) {
        if (msg.display_name !== lastSpeaker) {
          if (currentGroup){
            groups.push(currentGroup);
          }
          currentGroup = {
            speaker: msg.display_name,
            messages: [msg]
          };
          lastSpeaker = msg.display_name;
        } 
        else {
          currentGroup.messages.push(msg);
        }
      }
      if (currentGroup) groups.push(currentGroup);
      return groups;
  }
  },
  async mounted(){

    if (this.user){
      this.userName = this.user.name;
    }
  },

  async created() {
    await TranscriptService.getTranscriptList(this.token).then((data) => {
      this.transcripts = data.data.result.transcripts;
    });
    if (this.$route.params.id) {
      this.selectTranscript(this.$route.params.id);
    } else if (!this.$isMobile()) {
      this.selectTranscript(this.transcripts[0].conference_id);
    }
  },
  watch: {
    "selectedTranscript.is_shared": async function(newVal) {
      if (this.showShareModal) {
        await TranscriptService.updateTranscript(this.selectedTranscript, this.token).then(() => {
          if (newVal) {
            this.$toast.success('transcript_public_saved_msg');
          } else {
            this.$toast.success('transcript_private_saved_msg');
          }
        });
      }
    }
  }
} 
</script>
